import React, { useEffect } from "react";
import { useDisplayTimezone } from "hooks/useTimezone";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { T } from "services/lang/LanguageService";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { fmtDatetime, toInstant } from "utils/time-utils";
import DatasetDisplay from "../product-template-table/components/templates/dataset/DatasetDisplay";
import { getTextColor } from "../charts/utils";
import { DatasetStatusIndicatorProps, HighlightOptions } from "./DatasetStatusIndicatorTypes";

const convertToHighlightRanges = (
  options: HighlightOptions[],
  fallbackTextColor: string,
  fallbackBgColor: string
) =>
  options.map(opt => ({
    min: {
      value: opt.minValue,
      inclusive: opt.includeMinValue,
    },
    max: {
      value: opt.maxValue,
      inclusive: opt.includeMaxValue,
    },
    color: opt.color?.color ?? fallbackTextColor,
    bgColor: opt.color?.bgColor ?? fallbackBgColor,
    colorEntireRow: false,
  }));

export default function DatasetStatusIndicator(props: DatasetStatusIndicatorProps) {
  const displayTz = useDisplayTimezone();
  const setStatus = useComponentStateCallback(props);

  const { data, error, isLoading } = useGetRecentData([props.identifier], {
    count: 1,
    refTime: props.referenceTime?.toString(),
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
    },
  });

  useEffect(() => {
    if (!isLoading && data !== undefined) {
      setStatus({ state: "finished" });
    }
  }, [isLoading]);

  if (error) {
    easasAlert({
      summary: T("alert.failed-to-fetch-data"),
      detail: error.message,
      severity: "error",
    });
    setStatus({ state: "error", error: error.message });
  }

  const dataInfo = data?.[0];
  const value = dataInfo?.data[0].value;
  const unit = dataInfo?.unit;
  const stringValueMapper = dataInfo?.stringValueMapper;
  const title = dataInfo?.title;
  const decimalPlaces = dataInfo?.decimalPlaces;
  const datasetTime = dataInfo?.data[0].key;
  const defaultTextColor = props.defaultTextColor ?? "#000000";
  const defaultBgColor = props.defaultBgColor ?? "";

  const bgHighlightRanges = convertToHighlightRanges(
    props.highlights ?? [],
    defaultTextColor,
    defaultBgColor
  );
  const highlightColor = {
    color: props.defaultTextColor,
    bgColor: props.defaultBgColor,
  };

  const datasetInfo = `${title}: ${fmtDatetime(datasetTime, displayTz, "yyyy-MM-dd HH:mm:ss")}`;

  return (
    <div
      className="font-bold"
      style={{ color: getTextColor(props.theme) }}
      data-tooltip-id="dween-global-tooltip"
      data-tooltip-content={datasetInfo}
    >
      <DatasetDisplay
        identifier={props.identifier}
        value={value}
        unit={unit}
        title={title}
        stringValueMapper={stringValueMapper}
        decimalPlaces={decimalPlaces}
        time={toInstant(datasetTime) ?? null}
        maxAge={props.maxAge}
        highlightColor={highlightColor}
        bgHighlightRanges={bgHighlightRanges}
      />
    </div>
  );
}
